import React, {useCallback, useEffect, useRef, useState} from 'react'
import {SearchIcon, Close16FilledIcon, ChevronRightIcon, ManualBookIcon} from '../../icons'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {
    Text,
    Stack,
    HStack,
    useMultiStyleConfig,
    IconButton,
    Button,
    Box,
    Grid,
    Portal,
    useOutsideClick,
    useMediaQuery,
    Skeleton,
    Flex,
} from '@chakra-ui/react'
import {flushSync} from 'react-dom'
import Search from '../index'
import SearchModalProducts from './search-modal-products'
import routingObject from '../../../utils/static-routes'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useProductRegistrationHandler from '../../../custom-hooks/use-product-registration-handler'
import {useCustomPreferenceWithLocale} from '../../../hooks/use-custom-preference'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {decodeHtmlEntities} from '../../../utils/utils'
import {useLocation} from 'react-router-dom'

const SEARCH_BODY_LOCK_CLASS = 'search-surface-open'
const SEARCH_BODY_LOCK_COUNT_ATTR = 'data-search-surface-lock-count'
const MIN_SEARCH_CHARS = 3
const ACTIVE_SUGGESTIONS_LIMIT = 6
const SUGGESTION_MAX_CHARACTERS = 50

const normalizeSuggestionText = (value) =>
    decodeHtmlEntities(`${value || ''}`)
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim()

const truncateSuggestionText = (value) => {
    const normalizedText = normalizeSuggestionText(value)
    if (normalizedText.length <= SUGGESTION_MAX_CHARACTERS) return normalizedText
    return `${normalizedText.slice(0, SUGGESTION_MAX_CHARACTERS - 1)}…`
}

const normalizeCategoryLink = (rawLink) => {
    if (!rawLink) return ''

    const normalizedRawLink = `${rawLink}`.trim()
    if (!normalizedRawLink) return ''

    let path = normalizedRawLink

    if (/^https?:\/\//i.test(normalizedRawLink)) {
        try {
            const parsedUrl = new URL(normalizedRawLink)
            path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        } catch (error) {
            path = normalizedRawLink
        }
    }

    if (path.includes('/s/') && path.includes('/c/')) {
        path = path.slice(path.indexOf('/c/'))
    }

    if (path.includes('/category/')) {
        path = path.replace('/category/', '/c/')
    }

    if (!path.startsWith('/')) {
        path = `/${path}`
    }

    return path
}

const isTechnicalCategoryPath = (path) => /\/c\/commerceCategory_/i.test(`${path || ''}`)

const getResolvedCategoryLink = (suggestion, categoriesData) => {
    const categoryId = suggestion?.id
    const mappedCategoryLink = categoriesData?.[categoryId]?.url
    const categoryLink =
        mappedCategoryLink || suggestion?.link || (categoryId ? `/c/${categoryId}` : '')

    return normalizeCategoryLink(categoryLink)
}

const findMatchingCategorySuggestionByName = (suggestion, suggestions = []) => {
    const normalizedSuggestionName = normalizeSuggestionText(suggestion?.name).toLowerCase()
    if (!normalizedSuggestionName) return undefined

    return suggestions.find((entry) => {
        if (entry?.type !== 'category') return false
        return normalizeSuggestionText(entry?.name).toLowerCase() === normalizedSuggestionName
    })
}

const extractCuratedProductIds = (value) => {
    if (!value) return []

    if (typeof value === 'string') {
        const trimmed = value.trim()

        if (!trimmed) return []

        try {
            const parsed = JSON.parse(trimmed)
            return extractCuratedProductIds(parsed)
        } catch (error) {
            return trimmed
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
        }
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === 'string') return item.trim()
                if (!item || typeof item !== 'object') return null

                return item.productId || item.productID || item.id || item.sku || item.value || null
            })
            .filter(Boolean)
    }

    if (typeof value === 'object') {
        return extractCuratedProductIds(
            value.productIds || value.ids || value.products || value.hits || value.items || [],
        )
    }

    return []
}

const lockBodyScroll = () => {
    if (typeof document === 'undefined') return

    const body = document.body
    const currentLocks = Number(body.getAttribute(SEARCH_BODY_LOCK_COUNT_ATTR) || 0)
    const nextLocks = currentLocks + 1

    body.setAttribute(SEARCH_BODY_LOCK_COUNT_ATTR, String(nextLocks))
    body.classList.add(SEARCH_BODY_LOCK_CLASS)
    // The scrollable element is <html>, not <body>, on most pages here - lock both
    document.documentElement.classList.add(SEARCH_BODY_LOCK_CLASS)
}

const unlockBodyScroll = () => {
    if (typeof document === 'undefined') return

    const body = document.body
    const currentLocks = Number(body.getAttribute(SEARCH_BODY_LOCK_COUNT_ATTR) || 0)
    const nextLocks = Math.max(0, currentLocks - 1)

    if (nextLocks === 0) {
        body.removeAttribute(SEARCH_BODY_LOCK_COUNT_ATTR)
        body.classList.remove(SEARCH_BODY_LOCK_CLASS)
        document.documentElement.classList.remove(SEARCH_BODY_LOCK_CLASS)
        return
    }

    body.setAttribute(SEARCH_BODY_LOCK_COUNT_ATTR, String(nextLocks))
}

const QuickLinkShieldIcon = (props) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        {...props}
    >
        <path
            d="M5.99805 0.400391C6.24494 0.400391 6.48474 0.477099 6.68457 0.619141L6.76758 0.683594L6.77344 0.688477C7.58212 1.39535 8.69191 1.90039 9.49805 1.90039C9.75337 1.90039 9.99955 1.98897 10.1953 2.14941L10.2754 2.22266L10.3486 2.30273C10.5091 2.49845 10.5976 2.74472 10.5977 3V6.5C10.5976 7.90832 10.0982 8.99609 9.29297 9.82227C8.504 10.6317 7.44808 11.1646 6.36523 11.542L6.36035 11.5439C6.12517 11.6235 5.8702 11.6198 5.63672 11.5361V11.5371C4.55108 11.1622 3.49392 10.6311 2.7041 9.82227C1.89785 8.99652 1.3985 7.90833 1.39844 6.5V3C1.3985 2.7084 1.51454 2.42888 1.7207 2.22266L1.80078 2.14941C1.99654 1.98897 2.24272 1.90039 2.49805 1.90039L2.65234 1.89453C3.44178 1.83611 4.46953 1.34719 5.22363 0.688477L5.22852 0.683594C5.44303 0.500459 5.71598 0.400391 5.99805 0.400391ZM5.99805 1.60352C5.08862 2.39291 3.76248 3.06008 2.59766 3.09668V6.5C2.59771 7.59124 2.97277 8.37928 3.5625 8.9834C4.16408 9.59946 5.01671 10.0505 6.00586 10.3945C6.98765 10.0486 7.83485 9.59862 8.43359 8.98438C9.02313 8.37942 9.39838 7.59128 9.39844 6.5V3.09668C8.23432 3.06034 6.91276 2.39813 5.99805 1.60352ZM7.07422 4.57617C7.30853 4.34186 7.68756 4.34186 7.92188 4.57617C8.15619 4.81049 8.15619 5.18951 7.92188 5.42383L5.92188 7.42383C5.68756 7.65814 5.30853 7.65814 5.07422 7.42383L4.07422 6.42383C3.8399 6.18951 3.8399 5.81049 4.07422 5.57617C4.30853 5.34186 4.68756 5.34186 4.92188 5.57617L5.49805 6.15234L7.07422 4.57617Z"
            fill="currentColor"
        />
    </svg>
)

const QuickLinkManualsIcon = (props) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        {...props}
    >
        <path
            d="M7 6.5V4.25C7 3.5 7.5 3.5 7.5 2.5C7.5 2.10218 7.34196 1.72064 7.06066 1.43934C6.77936 1.15804 6.39782 1 6 1C5.60218 1 5.22064 1.15804 4.93934 1.43934C4.65804 1.72064 4.5 2.10218 4.5 2.5C4.5 3.5 5 3.5 5 4.25V6.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M10 7.75C10 7.41848 9.8683 7.10054 9.63388 6.86612C9.39946 6.6317 9.08152 6.5 8.75 6.5H3.25C2.91848 6.5 2.60054 6.6317 2.36612 6.86612C2.1317 7.10054 2 7.41848 2 7.75V8.5C2 8.63261 2.05268 8.75979 2.14645 8.85355C2.24021 8.94732 2.36739 9 2.5 9H9.5C9.63261 9 9.75979 8.94732 9.85355 8.85355C9.94732 8.75979 10 8.63261 10 8.5V7.75Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M2.5 11H9.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const QuickLinkServiceIcon = (props) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        {...props}
    >
        <path
            d="M6.2045 2.91195L4.793 4.29295C4.60553 4.48048 4.50021 4.73479 4.50021 4.99995C4.50021 5.26511 4.60553 5.51942 4.793 5.70695C4.98053 5.89442 5.23484 5.99974 5.5 5.99974C5.76516 5.99974 6.01947 5.89442 6.207 5.70695L7.062 4.85195C7.1739 4.74 7.30676 4.65119 7.45299 4.5906C7.59923 4.53001 7.75596 4.49883 7.91425 4.49883C8.07254 4.49883 8.22927 4.53001 8.3755 4.5906C8.52174 4.65119 8.6546 4.74 8.7665 4.85195L9.707 5.79295C9.89447 5.98048 9.99979 6.23479 9.99979 6.49995C9.99979 6.76511 9.89447 7.01942 9.707 7.20695C10.5 6.41395 11 5.74995 11 4.74995C11 4.19355 10.8312 3.65024 10.5159 3.19178C10.2007 2.73332 9.75374 2.38128 9.23419 2.18215C8.71465 1.98302 8.14692 1.94617 7.60599 2.07647C7.06507 2.20677 6.57639 2.49809 6.2045 2.91195ZM9.707 7.20695C9.60838 7.30558 9.4913 7.38382 9.36244 7.4372C9.23359 7.49058 9.09548 7.51805 8.956 7.51805C8.81652 7.51805 8.67841 7.49058 8.54955 7.4372C8.4207 7.38382 8.30362 7.30558 8.205 7.20695C8.31177 7.3035 8.3978 7.42075 8.45786 7.55158C8.51792 7.68241 8.55076 7.82408 8.55437 7.96799C8.55799 8.1119 8.53231 8.25504 8.4789 8.38872C8.42548 8.52239 8.34545 8.64382 8.24366 8.74561C8.14187 8.8474 8.02044 8.92743 7.88676 8.98085C7.75309 9.03426 7.60994 9.05994 7.46604 9.05632C7.32213 9.05271 7.18045 9.01987 7.04963 8.95981C6.9188 8.89975 6.80155 8.81372 6.705 8.70695C6.8037 8.80527 6.88205 8.92207 6.93558 9.05069C6.9891 9.17931 7.01675 9.31722 7.01693 9.45654C7.01712 9.59585 6.98984 9.73383 6.93666 9.86259C6.88348 9.99135 6.80544 10.1084 6.707 10.207C6.61199 10.302 6.49879 10.3769 6.37418 10.4272C6.24957 10.4775 6.11609 10.5022 5.98173 10.4998C5.84736 10.4974 5.71486 10.4679 5.59215 10.4131C5.46943 10.3583 5.35902 10.2794 5.2675 10.181L2.5 7.49995C1.75 6.74995 1 5.89995 1 4.74995C1.00011 4.19359 1.16897 3.65035 1.48428 3.19197C1.79959 2.73359 2.24651 2.38163 2.76604 2.18256C3.28556 1.98349 3.85325 1.94669 4.39413 2.077C4.93501 2.20732 5.42364 2.49862 5.7955 2.91245C5.85109 2.96412 5.9242 2.99279 6.0001 2.9927C6.07599 2.99261 6.14903 2.96375 6.2045 2.91195"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            opacity="0.1"
            d="M1.51953 3.60547L6.91953 9.00547"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <path
            opacity="0.1"
            d="M3.31641 2.40576L8.11641 7.20576"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
)

const SearchModal = () => {
    const iconStyles = useMultiStyleConfig('Header')
    const searchStyles = useMultiStyleConfig('Search', {})
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [panelTop, setPanelTop] = useState(0)
    const [isDesktop] = useMediaQuery('(min-width: 1200px)')
    const triggerRef = useRef(null)
    const searchSurfaceRef = useRef(null)
    const searchInputRef = useRef(null)
    const previousLocationRef = useRef(`${location.pathname}${location.search}`)
    const navigate = useNavigation()
    const categoriesData = useGetCategoriesData()
    const {locale} = useMultiSite()
    const handleProductRegistrationClick = useProductRegistrationHandler(routingObject)
    const {formatMessage} = useIntl()
    const buttonData = defineMessages({
        productRegistration: {
            id: 'search.modal.productRegistration',
            defaultMessage: 'Register your machine',
        },
        instructionManuals: {
            id: 'search.modal.instructionManuals',
            defaultMessage: 'Instruction manuals',
        },
        authorizedServiceCenters: {
            id: 'search.modal.authorizedServiceCenters',
            defaultMessage: 'Service and repairs',
        },
    })
    const [searchResults, setSearchResults] = useState([])
    // we dont want to show PDP only products in the search results only the search suggestions
    const filteredSearchResults =
        searchResults?.hits?.length > 0
            ? {
                  ...searchResults,
                  hits: searchResults?.hits?.filter(
                      (product) =>
                          product?.representedProduct?.c_skuIsPDPOnly === 'false' ||
                          product?.representedProduct?.c_isPDPOnlyPurchasable === 'true',
                  ),
              }
            : searchResults
    const [categorySuggestions, setCategorySuggestions] = useState([])
    const [modalNavigation, setModalNavigation] = useState('')
    const [showCategories, setShowCategories] = useState(true)
    const [searchInputValue, setSearchInputValue] = useState('')
    const [isSearchLoading, setIsSearchLoading] = useState(false)
    const [isSearchSubmitted, setIsSearchSubmitted] = useState(false)

    const curatedProductsRawPreference = useCustomPreferenceWithLocale('curatedProducts')

    // Extract IDs from preference
    const preferenceIds = extractCuratedProductIds(curatedProductsRawPreference).filter(Boolean)
    const curatedProductIds = preferenceIds.slice(0, 3)

    const trimmedQuery = searchInputValue.trim()
    const activeSuggestions = (categorySuggestions || [])
        .filter((s) => s?.type === 'category' || s?.type === 'manual')
        .slice(0, ACTIVE_SUGGESTIONS_LIMIT)
    const activeProducts = (filteredSearchResults?.hits || []).slice(0, 3)
    const hasSuggestions = activeSuggestions.length > 0
    const hasProducts = activeProducts.length > 0

    // Show curated products only when below the minimum search threshold
    const shouldShowCuratedProducts = searchInputValue.trim().length < MIN_SEARCH_CHARS

    // Default state is only when input is empty (no suggestions or results shown)
    const isDefaultState = searchInputValue.trim().length < MIN_SEARCH_CHARS

    const {
        data: curatedProductsData,
        isLoading: isCuratedProductsLoading,
        isFetching: isCuratedProductsFetching,
    } = useProducts(
        {
            parameters: {
                ids: curatedProductIds.join(','),
                expand: ['availability', 'images', 'prices', 'promotions'],
            },
            headers: {
                'x-debug': 'search-curated-products',
            },
        },
        {
            enabled: curatedProductIds.length > 0,
            retry: false,
            keepPreviousData: true,
            staleTime: Infinity,
        },
    )

    const filteredCuratedProducts = curatedProductsData?.data || []

    const showCuratedSkeleton =
        shouldShowCuratedProducts &&
        curatedProductIds.length > 0 &&
        (isCuratedProductsLoading || isCuratedProductsFetching)

    const showActiveStateProductSkeletons = !isDefaultState && isSearchLoading
    const shouldShowActiveProductsSection =
        !isDefaultState && (showActiveStateProductSkeletons || hasProducts)
    const activeProductSkeletonItems = Array.from({length: 3})
    const isZeroResults =
        trimmedQuery.length >= MIN_SEARCH_CHARS &&
        !isSearchLoading &&
        !hasSuggestions &&
        !hasProducts
    const hasSearchMatches = !isSearchLoading && (hasSuggestions || hasProducts)
    const zeroResultsMessageText = `${formatMessage(
        {
            id: 'search.modal.zeroResultsPrefix',
            defaultMessage: 'No matches for "{query}".',
        },
        {query: trimmedQuery},
    )} ${formatMessage({
        id: 'faq_page.search.error_message',
        defaultMessage: 'Try something different.',
    })}`

    const routeMap = {
        productRegistration: routingObject.PRODUCT_REGISTRATION_PATH,
        instructionManuals: routingObject.MANUALS_PATH,
        authorizedServiceCenters:
            locale?.alias?.toLowerCase() === 'en-gb'
                ? '/e/care-and-repair'
                : '/authorized-service-center',
    }

    const quickLinks = [
        {
            key: 'productRegistration',
            message: buttonData.productRegistration,
            icon: QuickLinkShieldIcon,
            onClick: () => {
                handleClose()
                handleProductRegistrationClick()
            },
        },
        {
            key: 'instructionManuals',
            message: buttonData.instructionManuals,
            icon: QuickLinkManualsIcon,
            onClick: () => {
                handleClose()
                navigate(routeMap.instructionManuals)
            },
        },
        {
            key: 'authorizedServiceCenters',
            message: buttonData.authorizedServiceCenters,
            icon: QuickLinkServiceIcon,
            onClick: () => {
                handleClose()
                navigate(routeMap.authorizedServiceCenters)
            },
        },
    ]
    const quickLinkIcons = [QuickLinkManualsIcon, QuickLinkServiceIcon, QuickLinkShieldIcon]

    const renderQuickLinks = (buttonVariant = 'link-button') => (
        <Stack {...searchStyles.quickLinksStack} data-search-quick-links="true">
            <Text {...searchStyles.quickLinksTitle}>
                <FormattedMessage defaultMessage="Quick Links" id="search.modal.quickLinks" />
            </Text>
            <Stack as="ul" {...searchStyles.quickLinksList}>
                {quickLinks.map((item, index) => (
                    <Box as="li" key={item.message.id} {...searchStyles.quickLinksListItem}>
                        <Button
                            variant={buttonVariant}
                            size="base"
                            onClick={item.onClick}
                            tabIndex={0}
                            sx={searchStyles.quickLinkButton}
                            aria-label={`${formatMessage(item.message)}`}
                        >
                            <Box
                                as={quickLinkIcons[index]}
                                {...searchStyles.quickLinkIcon}
                                sx={item.iconSx}
                            />
                            <Text {...searchStyles.quickLinkText}>
                                <FormattedMessage {...item.message} />
                            </Text>
                        </Button>
                    </Box>
                ))}
            </Stack>
        </Stack>
    )

    const handleSuggestionClick = (suggestion) => {
        if (suggestion?.type === 'category') {
            const categoryLink = getResolvedCategoryLink(suggestion, categoriesData)
            const fallbackCategoryQuery = normalizeSuggestionText(suggestion?.name)

            if (!categoryLink && !fallbackCategoryQuery) return

            handleClose()

            if (!categoryLink) {
                navigate(searchUrlBuilder(fallbackCategoryQuery))
                return
            }

            navigate(categoryLink)
            return
        }

        const suggestionLink = `${suggestion?.link || ''}`.trim()
        const isManualOrPdfSuggestion =
            suggestion?.type === 'manual' || /\.pdf(?:$|[?#])/i.test(suggestionLink)

        if (isManualOrPdfSuggestion && suggestionLink) {
            if (typeof window !== 'undefined') {
                window.open(suggestionLink, '_blank', 'noopener,noreferrer')
            }
            handleClose()
            return
        }

        if (suggestion?.type === 'product' && suggestionLink) {
            const fallbackPid = suggestion?.productId
            const pidSeparator = suggestionLink.includes('?') ? '&' : '?'
            const linkWithPid =
                fallbackPid && !/([?&])pid=/.test(suggestionLink)
                    ? `${suggestionLink}${pidSeparator}pid=${encodeURIComponent(fallbackPid)}`
                    : suggestionLink

            handleClose()
            navigate(linkWithPid)
            return
        }

        if (suggestion?.type === 'phrase') {
            const matchingCategorySuggestion = findMatchingCategorySuggestionByName(
                suggestion,
                categorySuggestions,
            )
            const matchingCategoryLink = getResolvedCategoryLink(
                matchingCategorySuggestion,
                categoriesData,
            )

            if (matchingCategoryLink) {
                handleClose()
                navigate(matchingCategoryLink)
                return
            }
        }

        const suggestionQuery = normalizeSuggestionText(
            suggestion?.name || suggestion?.productName || trimmedQuery,
        )
        if (!suggestionQuery) return

        handleClose()
        navigate(searchUrlBuilder(suggestionQuery))
    }

    const syncPanelPosition = useCallback(() => {
        if (!triggerRef.current) return

        const headerEl = document.getElementById('main-header')
        const sourceEl = headerEl || triggerRef.current
        const sourceBounds = sourceEl.getBoundingClientRect()

        setPanelTop(Math.max(0, Math.round(sourceBounds.bottom)))
    }, [])

    const resetSearchState = useCallback(() => {
        setSearchResults([]) // Clear search results
        setCategorySuggestions([]) // Optionally clear category suggestions
        setShowCategories(true) // Reset categories visibility if needed
        setSearchInputValue('')
        setIsSearchLoading(false)
        setIsSearchSubmitted(false) // Reset search submitted state
    }, [])

    const handleClose = useCallback(() => {
        resetSearchState()
        setIsOpen(false)
        unlockBodyScroll()
    }, [resetSearchState])

    const handleOpen = () => {
        if (isOpen) return

        syncPanelPosition()
        lockBodyScroll()

        // Keep open/focus in the same tap/click event loop for iOS virtual keyboard behavior.
        flushSync(() => {
            setIsOpen(true)
        })

        searchInputRef.current?.focus({preventScroll: true})
    }

    const handleTriggerPointerOpen = (event) => {
        if (isOpen) return

        event.preventDefault()
        handleOpen()
    }

    useOutsideClick({
        ref: searchSurfaceRef,
        handler: (event) => {
            if (!isOpen || !isDesktop) return
            if (triggerRef.current?.contains(event.target)) return
            handleClose()
        },
    })

    useEffect(() => {
        if (!isOpen) return
        const handleReposition = () => syncPanelPosition()
        window.addEventListener('resize', handleReposition)
        // Safety net: keep the panel glued to the header even if the scroll lock is bypassed
        window.addEventListener('scroll', handleReposition, {passive: true})

        return () => {
            window.removeEventListener('resize', handleReposition)
            window.removeEventListener('scroll', handleReposition)
        }
    }, [isOpen, syncPanelPosition])

    useEffect(() => {
        if (!isOpen) return
        searchInputRef.current?.focus({preventScroll: true})
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const onEscape = (event) => {
            if (event.key === 'Escape') {
                handleClose()
            }
        }

        document.addEventListener('keydown', onEscape)
        return () => document.removeEventListener('keydown', onEscape)
    }, [isOpen, handleClose])

    useEffect(() => {
        const currentLocationKey = `${location.pathname}${location.search}`

        if (!isOpen) {
            previousLocationRef.current = currentLocationKey
            return
        }

        if (previousLocationRef.current !== currentLocationKey) {
            handleClose()
        }

        previousLocationRef.current = currentLocationKey
    }, [location.pathname, location.search, isOpen, handleClose])

    useEffect(() => {
        return () => {
            unlockBodyScroll()
        }
    }, [])

    // Clear search results when returning to Default state (< 3 characters)
    useEffect(() => {
        if (isDefaultState && searchResults?.length > 0) {
            setSearchResults([])
        }
    }, [isDefaultState, searchResults])

    const handleActiveStateArrowNavigation = useCallback(
        (event) => {
            if (!isOpen || isDefaultState) return
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

            const focusableTargets = Array.from(
                searchSurfaceRef.current?.querySelectorAll(
                    '[data-search-nav-item="true"], [data-testid^="search-product-tile-"] a, [data-testid^="search-product-tile-"] button',
                ) || [],
            ).filter((element) => {
                if (!element) return false
                if (
                    element.hasAttribute('disabled') ||
                    element.getAttribute('aria-disabled') === 'true'
                ) {
                    return false
                }

                const isHidden =
                    element.getAttribute('tabindex') === '-1' ||
                    element.getAttribute('aria-hidden') === 'true'

                return !isHidden
            })

            if (focusableTargets.length === 0) return

            event.preventDefault()

            const currentIndex = focusableTargets.indexOf(document.activeElement)
            const direction = event.key === 'ArrowDown' ? 1 : -1

            const nextIndex =
                currentIndex === -1
                    ? direction === 1
                        ? 0
                        : focusableTargets.length - 1
                    : (currentIndex + direction + focusableTargets.length) % focusableTargets.length

            focusableTargets[nextIndex]?.focus()
        },
        [isOpen, isDefaultState],
    )

    return (
        <>
            <IconButton
                ref={triggerRef}
                aria-label={formatMessage({
                    id: 'global.search',
                    defaultMessage: 'Search',
                })}
                icon={<SearchIcon sx={iconStyles.headerIcon} />}
                {...iconStyles.buttonIcons}
                variant="ghost"
                onMouseDown={handleTriggerPointerOpen}
                onTouchStart={handleTriggerPointerOpen}
                onClick={handleOpen}
            />
            {isOpen && (
                <Portal>
                    <Box
                        {...searchStyles.searchSurfaceShell}
                        top={`${panelTop}px`}
                        data-testid="search-surface"
                    >
                        <Box {...searchStyles.searchSurfaceRow}>
                            <Box
                                ref={searchSurfaceRef}
                                {...searchStyles.modalContent}
                                {...searchStyles.searchSurfacePanel}
                                onKeyDown={handleActiveStateArrowNavigation}
                            >
                                <Box {...searchStyles.searchSurfaceInner}>
                                    <Box {...searchStyles.modalBody}>
                                        <HStack
                                            {...searchStyles.searchInputRow}
                                            id="search-modal-input-row"
                                        >
                                            <Box {...searchStyles.searchInputGrow}>
                                                <Search
                                                    searchInputPlaceholder={formatMessage({
                                                        id: 'search.modal.placeholder',
                                                        defaultMessage: 'Search in delonghi.com',
                                                    })}
                                                    isSearchModal
                                                    setSearchResults={setSearchResults}
                                                    setCategorySuggestions={setCategorySuggestions}
                                                    setModalNavigation={setModalNavigation}
                                                    setShowCategories={setShowCategories}
                                                    onCloseModal={handleClose}
                                                    onInputValueChange={setSearchInputValue}
                                                    onSearchLoadingChange={setIsSearchLoading}
                                                    setIsSearchSubmitted={setIsSearchSubmitted}
                                                    variant="modalSearch"
                                                    externalSearchInputRef={searchInputRef}
                                                    onSubmitCustom={(e) => {
                                                        e.preventDefault()
                                                        const query =
                                                            searchInputRef.current?.value?.trim()
                                                        if (
                                                            !query ||
                                                            query.length < MIN_SEARCH_CHARS ||
                                                            !hasSearchMatches
                                                        )
                                                            return
                                                        handleClose()
                                                        navigate(searchUrlBuilder(query))
                                                    }}
                                                />
                                            </Box>
                                            <IconButton
                                                icon={
                                                    <Close16FilledIcon
                                                        {...searchStyles.closeIcon}
                                                        size="small"
                                                    />
                                                }
                                                aria-label={formatMessage({
                                                    id: 'search.modal.aria.closeButton',
                                                    defaultMessage: 'Close search',
                                                })}
                                                sx={searchStyles.modalHeaderActionButton}
                                                onClick={handleClose}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === ' '
                                                    ) {
                                                        handleClose()
                                                    }
                                                }}
                                                variant="ghost"
                                                size="base"
                                                data-testid="close-modal"
                                            />
                                        </HStack>
                                        {isDefaultState && (
                                            <Stack
                                                {...searchStyles.defaultStateLayout}
                                                data-testid="search-modal-default-state"
                                            >
                                                <Stack {...searchStyles.defaultStateSideColumn}>
                                                    <Box {...searchStyles.defaultQuickLinksWrap}>
                                                        {renderQuickLinks()}
                                                    </Box>
                                                </Stack>
                                                <Stack
                                                    {...searchStyles.defaultStateMainColumn}
                                                    minH={showCuratedSkeleton ? '18rem' : 'auto'}
                                                >
                                                    {showCuratedSkeleton && (
                                                        <Stack
                                                            {...searchStyles.productsTitleButton}
                                                        >
                                                            <Text {...searchStyles.quickLinksTitle}>
                                                                <FormattedMessage
                                                                    defaultMessage="Products"
                                                                    id="search.modal.products"
                                                                />
                                                            </Text>
                                                            <Grid {...searchStyles.productdGrid}>
                                                                {Array.from({length: 3}).map(
                                                                    (_, index) => (
                                                                        <Stack
                                                                            key={`curated-skeleton-${index}`}
                                                                            {...searchStyles.curatedSkeletonCard}
                                                                        >
                                                                            <Skeleton
                                                                                {...searchStyles.curatedSkeletonImage}
                                                                            />
                                                                            <Skeleton
                                                                                {...searchStyles.curatedSkeletonTitle}
                                                                            />
                                                                            <Skeleton
                                                                                {...searchStyles.curatedSkeletonPrice}
                                                                            />
                                                                        </Stack>
                                                                    ),
                                                                )}
                                                            </Grid>
                                                        </Stack>
                                                    )}
                                                    {shouldShowCuratedProducts &&
                                                        !showCuratedSkeleton &&
                                                        filteredCuratedProducts?.length > 0 && (
                                                            <Stack
                                                                {...searchStyles.productsTitleButton}
                                                            >
                                                                <Text
                                                                    {...searchStyles.quickLinksTitle}
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Best Sellers"
                                                                        id="search.modal.bestSellers"
                                                                    />
                                                                </Text>
                                                                <Stack
                                                                    {...searchStyles.productGridScrollArea}
                                                                >
                                                                    <Grid
                                                                        {...searchStyles.productdGrid}
                                                                    >
                                                                        {filteredCuratedProducts
                                                                            .slice(0, 3)
                                                                            .map(
                                                                                (
                                                                                    product,
                                                                                    index,
                                                                                ) => (
                                                                                    <SearchModalProducts
                                                                                        key={
                                                                                            product?.productId ||
                                                                                            product?.id
                                                                                        }
                                                                                        product={
                                                                                            product
                                                                                        }
                                                                                        index={
                                                                                            index
                                                                                        }
                                                                                        onCloseModal={
                                                                                            handleClose
                                                                                        }
                                                                                        searchResults={{
                                                                                            query: '',
                                                                                            total: filteredCuratedProducts.length,
                                                                                        }}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                    </Grid>
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                </Stack>
                                            </Stack>
                                        )}
                                        {!isDefaultState && (
                                            <Box
                                                {...searchStyles.activeStateContainer}
                                                data-testid="search-modal-active-state"
                                            >
                                                {isZeroResults && (
                                                    <Stack
                                                        {...searchStyles.zeroResultsStack}
                                                        w="100%"
                                                    >
                                                        <Text
                                                            {...searchStyles.zeroResultsMessage}
                                                            w="100%"
                                                        >
                                                            {zeroResultsMessageText}
                                                        </Text>
                                                    </Stack>
                                                )}
                                                <Box {...searchStyles.activeStateLayout}>
                                                    <Stack {...searchStyles.activeStateSideColumn}>
                                                        <Box flex="1">
                                                            {!isDefaultState &&
                                                                showCategories &&
                                                                hasSuggestions && (
                                                                    <Stack
                                                                        {...searchStyles.trendingStack}
                                                                    >
                                                                        <Text
                                                                            {...searchStyles.suggestionsTitle}
                                                                        >
                                                                            <FormattedMessage
                                                                                defaultMessage="Suggestions"
                                                                                id="search.modal.suggestions"
                                                                            />
                                                                        </Text>
                                                                        <Stack
                                                                            {...searchStyles.activeSuggestionsList}
                                                                        >
                                                                            {activeSuggestions.map(
                                                                                (
                                                                                    suggestion,
                                                                                    index,
                                                                                ) => (
                                                                                    <Flex
                                                                                        key={`${
                                                                                            suggestion?.type ||
                                                                                            'category'
                                                                                        }-${
                                                                                            suggestion?.link ||
                                                                                            suggestion?.name ||
                                                                                            index
                                                                                        }`}
                                                                                        {...searchStyles.suggestionContainer}
                                                                                    >
                                                                                        <Button
                                                                                            variant="link-button-no-underline"
                                                                                            onClick={() =>
                                                                                                handleSuggestionClick(
                                                                                                    suggestion,
                                                                                                )
                                                                                            }
                                                                                            onKeyDown={(
                                                                                                e,
                                                                                            ) => {
                                                                                                if (
                                                                                                    e.key ===
                                                                                                        'Enter' ||
                                                                                                    e.key ===
                                                                                                        ' '
                                                                                                ) {
                                                                                                    handleSuggestionClick(
                                                                                                        suggestion,
                                                                                                    )
                                                                                                }
                                                                                            }}
                                                                                            tabIndex={
                                                                                                0
                                                                                            }
                                                                                            data-search-nav-item="true"
                                                                                            sx={
                                                                                                searchStyles.activeSuggestionButton
                                                                                            }
                                                                                            aria-label={
                                                                                                suggestion?.type ===
                                                                                                'manual'
                                                                                                    ? `Open manual: ${truncateSuggestionText(
                                                                                                          suggestion?.name,
                                                                                                      )}`
                                                                                                    : `Search: ${truncateSuggestionText(
                                                                                                          suggestion?.name,
                                                                                                      )}`
                                                                                            }
                                                                                        >
                                                                                            {suggestion?.type ===
                                                                                                'manual' && (
                                                                                                <ManualBookIcon
                                                                                                    data-testid="suggestion-manual-icon"
                                                                                                    {...searchStyles.suggestionIcon}
                                                                                                />
                                                                                            )}

                                                                                            <Text
                                                                                                {...searchStyles.activeSuggestionText}
                                                                                            >
                                                                                                {truncateSuggestionText(
                                                                                                    suggestion?.name,
                                                                                                )}
                                                                                            </Text>
                                                                                        </Button>
                                                                                    </Flex>
                                                                                ),
                                                                            )}
                                                                        </Stack>
                                                                    </Stack>
                                                                )}
                                                        </Box>
                                                        <Box {...searchStyles.activeQuickLinksWrap}>
                                                            {renderQuickLinks()}
                                                        </Box>
                                                    </Stack>

                                                    {!isSearchSubmitted &&
                                                        shouldShowCuratedProducts &&
                                                        !showCuratedSkeleton &&
                                                        filteredCuratedProducts?.length > 0 && (
                                                            <Stack
                                                                {...searchStyles.productsTitleButton}
                                                                {...searchStyles.activeStateMainColumn}
                                                            >
                                                                <Text
                                                                    {...searchStyles.quickLinksTitle}
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Best Sellers"
                                                                        id="search.modal.bestSellers"
                                                                    />
                                                                </Text>
                                                                <Stack
                                                                    {...searchStyles.productGridScrollArea}
                                                                >
                                                                    <Grid
                                                                        {...searchStyles.productdGrid}
                                                                    >
                                                                        {filteredCuratedProducts
                                                                            .slice(0, 3)
                                                                            .map(
                                                                                (
                                                                                    product,
                                                                                    index,
                                                                                ) => (
                                                                                    <SearchModalProducts
                                                                                        key={
                                                                                            product?.productId ||
                                                                                            product?.id
                                                                                        }
                                                                                        product={
                                                                                            product
                                                                                        }
                                                                                        index={
                                                                                            index
                                                                                        }
                                                                                        onCloseModal={
                                                                                            handleClose
                                                                                        }
                                                                                        searchResults={{
                                                                                            query: '',
                                                                                            total: filteredCuratedProducts.length,
                                                                                        }}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                    </Grid>
                                                                </Stack>
                                                            </Stack>
                                                        )}

                                                    {shouldShowActiveProductsSection && (
                                                        <Stack
                                                            {...searchStyles.productsTitleButton}
                                                            {...searchStyles.activeStateMainColumn}
                                                        >
                                                            <HStack {...searchStyles.topResultsRow}>
                                                                <Text
                                                                    {...searchStyles.topResultsTitle}
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Top results"
                                                                        id="search.modal.topResults"
                                                                    />
                                                                </Text>
                                                                <Button
                                                                    onClick={() => {
                                                                        if (
                                                                            !modalNavigation ||
                                                                            !hasSearchMatches
                                                                        )
                                                                            return
                                                                        handleClose()
                                                                        navigate(modalNavigation)
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (
                                                                            (e.key === 'Enter' ||
                                                                                e.key === ' ') &&
                                                                            modalNavigation &&
                                                                            hasSearchMatches
                                                                        ) {
                                                                            handleClose()
                                                                            navigate(
                                                                                modalNavigation,
                                                                            )
                                                                        }
                                                                    }}
                                                                    tabIndex={0}
                                                                    data-search-nav-item="true"
                                                                    sx={
                                                                        searchStyles.topResultsAction
                                                                    }
                                                                    variant="link-button-no-underline"
                                                                    size="small"
                                                                    aria-label={formatMessage({
                                                                        id: 'search.modal.aria.viewAllProducts',
                                                                        defaultMessage:
                                                                            'View all products',
                                                                    })}
                                                                    isDisabled={
                                                                        !modalNavigation ||
                                                                        !hasSearchMatches
                                                                    }
                                                                >
                                                                    <Text
                                                                        {...searchStyles.topResultsActionText}
                                                                    >
                                                                        <FormattedMessage
                                                                            defaultMessage="View all"
                                                                            id="search.modal.viewAll"
                                                                        />
                                                                    </Text>
                                                                    <ChevronRightIcon
                                                                        {...searchStyles.topResultsActionIcon}
                                                                    />
                                                                </Button>
                                                            </HStack>

                                                            <Stack
                                                                {...searchStyles.productGridScrollArea}
                                                            >
                                                                <Box position="relative">
                                                                    {hasProducts && (
                                                                        <Grid
                                                                            {...searchStyles.productdGrid}
                                                                        >
                                                                            {activeProducts.map(
                                                                                (
                                                                                    product,
                                                                                    index,
                                                                                ) => (
                                                                                    <SearchModalProducts
                                                                                        key={
                                                                                            product?.productId
                                                                                        }
                                                                                        product={
                                                                                            product
                                                                                        }
                                                                                        index={
                                                                                            index
                                                                                        }
                                                                                        onCloseModal={
                                                                                            handleClose
                                                                                        }
                                                                                        searchResults={
                                                                                            filteredSearchResults
                                                                                        }
                                                                                    />
                                                                                ),
                                                                            )}
                                                                        </Grid>
                                                                    )}

                                                                    {showActiveStateProductSkeletons && (
                                                                        <Box
                                                                            position={
                                                                                hasProducts
                                                                                    ? 'absolute'
                                                                                    : 'relative'
                                                                            }
                                                                            top={0}
                                                                            left={0}
                                                                            right={0}
                                                                            bottom={0}
                                                                            zIndex={1}
                                                                            pointerEvents="none"
                                                                            background={
                                                                                hasProducts
                                                                                    ? 'background.neutral.default'
                                                                                    : 'transparent'
                                                                            }
                                                                            opacity={
                                                                                hasProducts
                                                                                    ? 0.72
                                                                                    : 1
                                                                            }
                                                                        >
                                                                            <Grid
                                                                                {...searchStyles.productdGrid}
                                                                            >
                                                                                {activeProductSkeletonItems.map(
                                                                                    (_, index) => (
                                                                                        <Stack
                                                                                            key={`active-product-skeleton-${index}`}
                                                                                            {...searchStyles.curatedSkeletonCard}
                                                                                        >
                                                                                            <Skeleton
                                                                                                {...searchStyles.curatedSkeletonImage}
                                                                                            />
                                                                                            <Skeleton
                                                                                                {...searchStyles.curatedSkeletonTitle}
                                                                                            />
                                                                                            <Skeleton
                                                                                                {...searchStyles.curatedSkeletonPrice}
                                                                                            />
                                                                                        </Stack>
                                                                                    ),
                                                                                )}
                                                                            </Grid>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Stack>
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Portal>
            )}
        </>
    )
}
SearchModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
}

export default SearchModal

export const __TESTING__ = {
    getResolvedCategoryLink,
    isTechnicalCategoryPath,
    normalizeCategoryLink,
    findMatchingCategorySuggestionByName,
}
